import { useEffect, useState } from "react";
import { getJson } from "./http";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-level cache so every component that mounts during a single
// page load shares one in-flight request + the same resolved value.
// The admin save flow can call `bumpPerOrderLimitCache` after a PUT to
// force the next read to re-fetch immediately.
let _cached = null;
let _inflight = null;
const _listeners = new Set();

const DEFAULT_LIMIT = 20;

async function _fetchOnce() {
  if (_cached !== null) return _cached;
  if (_inflight) return _inflight;
  _inflight = getJson(`${API}/order-limit-info`)
    .then((data) => {
      const v = Number(data?.per_order_limit);
      _cached = Number.isFinite(v) && v >= 1 ? Math.floor(v) : DEFAULT_LIMIT;
      _inflight = null;
      _listeners.forEach((cb) => cb(_cached));
      return _cached;
    })
    .catch(() => {
      _cached = DEFAULT_LIMIT;
      _inflight = null;
      _listeners.forEach((cb) => cb(_cached));
      return _cached;
    });
  return _inflight;
}

export function bumpPerOrderLimitCache(next) {
  // Called by the admin Save handler so live storefront tabs (and the
  // admin's own preview tab) pick up the new ceiling without a reload.
  const n = Number(next);
  if (Number.isFinite(n) && n >= 1) {
    _cached = Math.floor(n);
    _listeners.forEach((cb) => cb(_cached));
  } else {
    _cached = null;
    _fetchOnce();
  }
}

// Hook returns the resolved cap (or the safe DEFAULT_LIMIT until the
// first /order-limit-info response lands). Components that need a
// numeric value before mount can read DEFAULT_LIMIT directly.
export function usePerOrderLimit() {
  const [limit, setLimit] = useState(() => _cached ?? DEFAULT_LIMIT);
  useEffect(() => {
    let alive = true;
    const onChange = (v) => {
      if (alive) setLimit(v);
    };
    _listeners.add(onChange);
    _fetchOnce().then((v) => {
      if (alive) setLimit(v);
    });
    return () => {
      alive = false;
      _listeners.delete(onChange);
    };
  }, []);
  return limit;
}

export { DEFAULT_LIMIT as PER_ORDER_LIMIT_FALLBACK };

// Synchronous read for non-React utility callers (CartContext's addItem/
// replaceItems). Returns the last-known cap, falling back to
// DEFAULT_LIMIT until the first /order-limit-info response lands. Safe
// to read at any time — never throws, never returns null.
export function getPerOrderLimitSync() {
  return _cached ?? DEFAULT_LIMIT;
}

// Kick off the initial fetch as soon as this module is first imported,
// so the cap is populated before the first cart mutation happens.
_fetchOnce();
